import React from 'react';

const SelfHosting = () => {
  return (
    <div className="doc-content">
      <div className="quickstart-header">
        <p className="quickstart-category">Getting Started</p>
        <h1>Self-Hosting</h1>
        <p className="quickstart-subtitle">Take full control of your MemU deployment with our self-hosting solution</p>
      </div>
      
      <div className="overview-section">
        <p>For organizations and developers who prioritize <strong>data sovereignty</strong>, <strong>customization freedom</strong>, and <strong>operational control</strong>, our self-hosting solution provides the complete MemU experience on your own infrastructure.</p>
        
        <p>The Community Edition empowers you with <strong>complete ownership</strong> of your memory infrastructure, enabling unlimited customization and ensuring your data never leaves your environment.</p>
      </div>
      
      <div className="platform-cards">
        <div className="platform-card">
          <div className="card-icon">
            <i className="fa-solid fa-shield-alt"></i>
          </div>
          <h3>Data Privacy</h3>
          <p>Keep sensitive data within your infrastructure with complete privacy control.</p>
        </div>
        
        <div className="platform-card">
          <div className="card-icon">
            <i className="fa-solid fa-wrench"></i>
          </div>
          <h3>Customization</h3>
          <p>Modify and extend the platform to fit your specific needs and requirements.</p>
        </div>
      </div>
      
      <div className="platform-cards">
        <div className="platform-card">
          <div className="card-icon">
            <i className="fa-solid fa-dollar-sign"></i>
          </div>
          <h3>Cost Control</h3>
          <p>Avoid recurring cloud fees for large-scale deployments with self-hosted infrastructure.</p>
        </div>
        
        <div className="platform-card">
          <div className="card-icon">
            <i className="fa-solid fa-lock"></i>
          </div>
          <h3>Compliance</h3>
          <p>Meet strict data residency and security requirements with on-premises deployment.</p>
        </div>
      </div>

      <div className="platform-section">
        <h2>What's Included</h2>
        <p>Our comprehensive self-hosting package will provide everything you need for a production-ready MemU deployment:</p>
        
        <div className="platform-cards">
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-solid fa-download"></i>
            </div>
            <h3>Easy Installation</h3>
            <p>One-click Docker deployment and comprehensive installation guides for various platforms.</p>
          </div>
          
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-solid fa-cogs"></i>
            </div>
            <h3>Full Configuration</h3>
            <p>Complete control over database, caching, security settings, and performance tuning.</p>
          </div>
          
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-solid fa-chart-line"></i>
            </div>
            <h3>Monitoring & Analytics</h3>
            <p>Built-in dashboards for performance monitoring, usage analytics, and system health.</p>
          </div>
          
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-solid fa-life-ring"></i>
            </div>
            <h3>Support & Documentation</h3>
            <p>Comprehensive documentation, troubleshooting guides, and community support.</p>
          </div>
        </div>
        
        
        
        <h2>Get Early Access</h2>
        
        <p>Join our community and be among the first to experience self-hosted MemU when it launches:</p>
        
        <div className="platform-cards">
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-brands fa-github"></i>
            </div>
            <h3>Watch on GitHub</h3>
            <p>Star and watch our repository to get notified about releases.</p>
            <a href="https://github.com/NevaMind-AI/memU" className="getting-started-link" target="_blank" rel="noopener noreferrer">Watch Repository</a>
          </div>
          
          <div className="platform-card">
            <div className="card-icon">
              <i className="fa-brands fa-discord"></i>
            </div>
            <h3>Join Discord</h3>
            <p>Get updates and discuss self-hosting in our #self-hosting channel.</p>
            <a href="https://discord.com/invite/hQZntfGsbJ" className="getting-started-link" target="_blank" rel="noopener noreferrer">Join Discord →</a>
          </div>
          
        </div>
        
        <h2>Beta Testing Program</h2>
        
        <div className="info-banner" style={{backgroundColor: '#f0fdf4', borderLeft: '4px solid #10b981'}}>
          <i className="fa-solid fa-flask" style={{color: '#10b981'}}></i>
          <div>
            <p><strong>Shape the Future of Self-Hosting</strong></p>
            <p>Join our exclusive beta program to test early releases, provide feedback, and help us build the perfect self-hosting experience.</p>
            <p>Apply at <a href="mailto:contact@nevamind.ai" style={{color: '#10b981', fontWeight: 'bold'}}>contact@nevamind.ai</a></p>
          </div>
        </div>
        
        <h2>Key Benefits</h2>
        
        <p>Transform your memory infrastructure with these powerful advantages:</p>
        
        <div className="benefits-grid">
          <div className="benefit-item">
            <i className="fa-solid fa-database"></i>
            <div>
              <h4>Complete Data Sovereignty</h4>
              <p>Maintain absolute control over your memory data with zero third-party dependencies</p>
            </div>
          </div>
          
          <div className="benefit-item">
            <i className="fa-solid fa-code"></i>
            <div>
              <h4>Unlimited Customization</h4>
              <p>Modify, extend, and integrate MemU seamlessly with your existing infrastructure</p>
            </div>
          </div>
          
          <div className="benefit-item">
            <i className="fa-solid fa-chart-line"></i>
            <div>
              <h4>Cost Optimization</h4>
              <p>Eliminate recurring cloud fees and scale cost-effectively on your own hardware</p>
            </div>
          </div>
          
          <div className="benefit-item">
            <i className="fa-solid fa-shield-check"></i>
            <div>
              <h4>Enterprise Compliance</h4>
              <p>Meet GDPR, HIPAA, SOC2, and other regulatory requirements with on-premises deployment</p>
            </div>
          </div>
          
          <div className="benefit-item">
            <i className="fa-solid fa-tachometer-alt"></i>
            <div>
              <h4>Performance Control</h4>
              <p>Optimize latency, throughput, and resource allocation for your specific workloads</p>
            </div>
          </div>
          
          <div className="benefit-item">
            <i className="fa-solid fa-network-wired"></i>
            <div>
              <h4>Air-Gap Compatible</h4>
              <p>Deploy in isolated environments with no internet connectivity requirements</p>
            </div>
          </div>
        </div>
        
        <h2>Technical Requirements</h2>
        
        <p>Our self-hosting solution is designed to work with modern infrastructure:</p>
        
        <div className="requirements-grid">
          <div className="requirement-item">
            <h4>🐳 Container Ready</h4>
            <p>Docker and Kubernetes support for easy deployment and scaling</p>
          </div>
          
          <div className="requirement-item">
            <h4>☁️ Cloud Native</h4>
            <p>Compatible with AWS, GCP, Azure, and on-premises infrastructure</p>
          </div>
          
          <div className="requirement-item">
            <h4>💾 Database Flexibility</h4>
            <p>Support for PostgreSQL, MySQL, and other popular database engines</p>
          </div>
          
          <div className="requirement-item">
            <h4>📊 Monitoring Integration</h4>
            <p>Built-in support for Prometheus, Grafana, and other monitoring tools</p>
          </div>
        </div>
        
        <div className="timeline-banner" style={{backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '24px', marginTop: '32px'}}>
          <h3 style={{margin: '0 0 12px 0', color: '#1a202c'}}>📅 Release Timeline</h3>
          <p style={{margin: '0', color: '#4a5568'}}>Self-hosting is planned for <strong>Q2 2025</strong>. Join our beta program to get early access and help shape the final release.</p>
        </div>
      </div>
    </div>
  );
};

export default SelfHosting;